<template>
  <div class="consulta-wrapper" @click="focusInput">

    <!-- Header -->
    <div class="consulta-header">
      <div class="consulta-header-left">
        <v-icon color="#555" size="18" class="mr-2">mdi-store-outline</v-icon>
        <span class="consulta-sucursal">{{ sucursal ? sucursal.nombre : '' }}</span>
      </div>
      <div class="consulta-header-center">CONSULTA DE PRECIOS</div>
      <div class="consulta-header-right">
        <span class="consulta-hora">{{ hora }}</span>
      </div>
    </div>

    <!-- Área principal -->
    <div class="consulta-main">

      <!-- Estado vacío -->
      <transition name="fade">
        <div v-if="estadoInicial && !cargando && !noEncontrado && !producto" class="consulta-empty">
          <v-icon size="96" color="#2a2a2a">mdi-barcode-scan</v-icon>
          <div class="consulta-empty-title">Escanee un producto</div>
          <div class="consulta-empty-sub">Acerque el lector al código de barras</div>
        </div>
      </transition>

      <!-- Cargando -->
      <transition name="fade">
        <div v-if="cargando" class="consulta-empty">
          <v-progress-circular indeterminate color="#444" size="64" width="3" />
          <div class="consulta-empty-sub mt-6">Buscando...</div>
        </div>
      </transition>

      <!-- No encontrado -->
      <transition name="fade">
        <div v-if="noEncontrado && !cargando" class="consulta-empty">
          <v-icon size="80" color="#333">mdi-alert-circle-outline</v-icon>
          <div class="consulta-empty-title" style="color:#555">Producto no encontrado</div>
          <div class="consulta-empty-sub">Código: {{ ultimoCodigo }}</div>
        </div>
      </transition>

      <!-- Producto encontrado -->
      <transition name="slide-up">
        <div v-if="producto && !cargando" class="consulta-producto">

          <!-- Imagen -->
          <div class="consulta-imagen-wrap">
            <v-img
              v-if="producto.imagen"
              :src="producto.imagen"
              contain
              class="consulta-imagen"
              max-height="340"
              max-width="340"
            />
            <div v-else class="consulta-imagen-placeholder">
              <v-icon size="120" color="#2a2a2a">mdi-package-variant</v-icon>
            </div>
          </div>

          <!-- Info -->
          <div class="consulta-info">
            <div class="consulta-nombre">{{ producto.descripcion }}</div>
            <div v-if="producto.detalle" class="consulta-detalle">{{ producto.detalle }}</div>
            <div v-if="producto.unidad_medida" class="consulta-unidad">
              <v-chip x-small outlined color="#444" text-color="#777" class="mt-1">
                {{ producto.unidad_medida }}
              </v-chip>
            </div>

            <div class="consulta-precio-wrap">
              <span class="consulta-precio-signo">$</span>
              <span class="consulta-precio">{{ formatPrecio(producto.precio_venta) }}</span>
            </div>

            <div v-if="producto.codigo_de_barra" class="consulta-codigo">
              <v-icon size="14" color="#444" class="mr-1">mdi-barcode</v-icon>
              {{ producto.codigo_de_barra }}
            </div>
          </div>

        </div>
      </transition>
    </div>

    <!-- Barra inferior con input oculto -->
    <div class="consulta-footer">
      <div class="consulta-input-wrap">
        <v-icon color="#333" class="mr-3">mdi-barcode-scan</v-icon>
        <input
          ref="barcodeInput"
          v-model="inputBarcode"
          class="consulta-input"
          placeholder="Escanee o ingrese el código..."
          @keyup.enter="buscar"
          autocomplete="off"
          inputmode="numeric"
        />
        <v-btn
          v-if="inputBarcode"
          icon
          small
          color="#444"
          @click.stop="limpiarInput"
        >
          <v-icon size="16">mdi-close</v-icon>
        </v-btn>
      </div>

      <!-- Barra de reset -->
      <div v-if="producto" class="consulta-reset-bar">
        <div class="consulta-reset-progreso" :style="{ width: resetProgress + '%' }" />
      </div>
    </div>

  </div>
</template>

<script>
import { mapState } from 'vuex';
import ProductosApi from '@/api/productos';

export default {
  name: 'ConsultaPrecios',

  data() {
    return {
      inputBarcode: '',
      producto: null,
      cargando: false,
      noEncontrado: false,
      estadoInicial: true,
      ultimoCodigo: '',
      hora: '',
      _horaTimer: null,
      _resetTimer: null,
      _resetInterval: null,
      resetProgress: 100,
      RESET_SEGUNDOS_ENCONTRADO: 8,
      RESET_SEGUNDOS_NO_ENCONTRADO: 4,
    };
  },

  computed: {
    ...mapState('sucursales', ['sucursal']),
  },

  methods: {
    focusInput() {
      this.$refs.barcodeInput && this.$refs.barcodeInput.focus();
    },

    limpiarInput() {
      this.inputBarcode = '';
      this.focusInput();
    },

    formatPrecio(valor) {
      if (valor == null) return '—';
      return Number(valor).toLocaleString('es-AR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    async buscar() {
      const codigo = this.inputBarcode.trim();
      if (!codigo) return;

      this.ultimoCodigo = codigo;
      this.inputBarcode = '';
      this.producto = null;
      this.noEncontrado = false;
      this.estadoInicial = false;
      this.cargando = true;
      this._cancelarReset();

      try {
        const uuid_s = this.sucursal ? this.sucursal.uuid : null;
        const isBarcode = /^\d{6,14}$/.test(codigo);
        const res = await ProductosApi.getPspList(codigo, uuid_s, 1, 1, isBarcode);
        const resultados = res.data.results || res.data;

        if (Array.isArray(resultados) && resultados.length > 0) {
          this.producto = resultados[0];
          this._iniciarReset(this.RESET_SEGUNDOS_ENCONTRADO);
        } else {
          this.noEncontrado = true;
          this._iniciarReset(this.RESET_SEGUNDOS_NO_ENCONTRADO);
        }
      } catch (e) {
        this.noEncontrado = true;
        this._iniciarReset(this.RESET_SEGUNDOS_NO_ENCONTRADO);
      } finally {
        this.cargando = false;
        this.focusInput();
      }
    },

    _iniciarReset(segundos) {
      this._cancelarReset();
      this.resetProgress = 100;
      const intervalo = 100;
      const pasos = (segundos * 1000) / intervalo;
      const decremento = 100 / pasos;

      this._resetInterval = setInterval(() => {
        this.resetProgress -= decremento;
        if (this.resetProgress <= 0) {
          this._cancelarReset();
          this.producto = null;
          this.noEncontrado = false;
          this.estadoInicial = true;
        }
      }, intervalo);
    },

    _cancelarReset() {
      if (this._resetInterval) {
        clearInterval(this._resetInterval);
        this._resetInterval = null;
      }
    },

    _actualizarHora() {
      this.hora = new Date().toLocaleTimeString('es-AR', {
        hour: '2-digit',
        minute: '2-digit',
      });
    },
  },

  mounted() {
    this.focusInput();
    this._actualizarHora();
    this._horaTimer = setInterval(this._actualizarHora, 30000);
    document.body.classList.add('consulta-precios-activa');
  },

  beforeDestroy() {
    clearInterval(this._horaTimer);
    this._cancelarReset();
    document.body.classList.remove('consulta-precios-activa');
  },
};
</script>

<style scoped>
/* ── Wrapper ── */
.consulta-wrapper {
  min-height: calc(100vh - 48px);
  background: #0f0f0f;
  display: flex;
  flex-direction: column;
  cursor: default;
  user-select: none;
}

/* ── Header ── */
.consulta-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 28px;
  border-bottom: 1px solid #1a1a1a;
  background: #111;
}

.consulta-header-left {
  display: flex;
  align-items: center;
  min-width: 180px;
}

.consulta-sucursal {
  font-size: 0.78rem;
  color: #555;
  font-weight: 500;
  letter-spacing: 0.02em;
}

.consulta-header-center {
  font-size: 0.7rem;
  font-weight: 700;
  color: #333;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  text-align: center;
}

.consulta-header-right {
  min-width: 180px;
  text-align: right;
}

.consulta-hora {
  font-size: 0.9rem;
  font-weight: 700;
  color: #333;
  font-variant-numeric: tabular-nums;
}

/* ── Main ── */
.consulta-main {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 48px;
  position: relative;
}

/* ── Estado vacío ── */
.consulta-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  position: absolute;
}

.consulta-empty-title {
  font-size: 1.4rem;
  font-weight: 600;
  color: #333;
  margin-top: 8px;
}

.consulta-empty-sub {
  font-size: 0.85rem;
  color: #3a3a3a;
}

/* ── Producto ── */
.consulta-producto {
  display: flex;
  align-items: center;
  gap: 64px;
  width: 100%;
  max-width: 900px;
}

.consulta-imagen-wrap {
  flex-shrink: 0;
  width: 300px;
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #161616;
  border: 1px solid #1e1e1e;
  border-radius: 12px;
  overflow: hidden;
}

.consulta-imagen {
  width: 100%;
  height: 100%;
}

.consulta-imagen-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.consulta-info {
  flex: 1;
  min-width: 0;
}

.consulta-nombre {
  font-size: 2rem;
  font-weight: 700;
  color: #e0e0e0;
  line-height: 1.2;
  margin-bottom: 10px;
}

.consulta-detalle {
  font-size: 1rem;
  color: #555;
  line-height: 1.5;
  margin-bottom: 8px;
}

.consulta-unidad {
  margin-bottom: 12px;
}

.consulta-precio-wrap {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 28px;
  margin-bottom: 16px;
}

.consulta-precio-signo {
  font-size: 2.4rem;
  font-weight: 700;
  color: #4caf50;
  line-height: 1;
  margin-top: 12px;
}

.consulta-precio {
  font-size: 5rem;
  font-weight: 800;
  color: #4caf50;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  letter-spacing: -2px;
}

.consulta-codigo {
  font-size: 0.75rem;
  color: #3a3a3a;
  display: flex;
  align-items: center;
  margin-top: 8px;
}

/* ── Footer ── */
.consulta-footer {
  border-top: 1px solid #1a1a1a;
  background: #111;
}

.consulta-input-wrap {
  display: flex;
  align-items: center;
  padding: 14px 28px;
}

.consulta-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  font-size: 0.9rem;
  color: #444;
  caret-color: #555;
  font-family: inherit;
}

.consulta-input::placeholder {
  color: #2a2a2a;
}

/* ── Barra de reset ── */
.consulta-reset-bar {
  height: 2px;
  background: #1a1a1a;
  position: relative;
  overflow: hidden;
}

.consulta-reset-progreso {
  height: 100%;
  background: #2d6a2d;
  transition: width 0.1s linear;
}

/* ── Transiciones ── */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.slide-up-enter-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}
.slide-up-enter {
  opacity: 0;
  transform: translateY(16px);
}
.slide-up-leave-active {
  transition: opacity 0.2s ease;
}
.slide-up-leave-to {
  opacity: 0;
}
</style>

<style>
/* ── Toolbar oculto solo en consulta de precios ── */
.consulta-precios-activa .v-app-bar {
  transform: translateY(-100%) !important;
  opacity: 0 !important;
  transition: transform 0.25s ease, opacity 0.25s ease !important;
  pointer-events: none;
}
.consulta-precios-activa .v-app-bar:hover {
  transform: translateY(0) !important;
  opacity: 1 !important;
  pointer-events: all;
}
/* Eliminar el padding-top que Vuetify reserva para el app-bar */
.consulta-precios-activa .v-main {
  padding-top: 0 !important;
}
</style>
